import { useRef, useState } from 'react';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-hot-toast';
import AccountSelectModal from '../modals/AccountSelectModal';
import QuickExpenseModal from '../modals/QuickExpenseModal';
import QuickMaterialConsumptionModal from '../modals/QuickMaterialConsumptionModal';
import useOperationStore from '../../store/useOperationStore';
import { formatCurrency } from '../../utils/formatters';

const money = (value, currency) => formatCurrency(Number(value || 0), currency);
const inputClass = 'mt-1 w-full rounded-xl border border-slate-200 p-3';
const currencyOptions = ['TRY', 'USD', 'EUR', 'GBP'];
const moneyFields = [['dailyFee', 'Günlük Ücret'], ['extraFee', 'Ek Ücret']];

const OperationStageTab = ({ operation, stage, invoices, consumptions = [], canAssign, canDeletePersonnel, canExpense, canConsume, onRefresh, onOpenInvoice, expensesLoading, expensesUnavailable }) => {
  const [draft, setDraft] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [consumptionOpen, setConsumptionOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const submitting = useRef(false);
  const saveStagePersonnel = useOperationStore((state) => state.saveStagePersonnel);
  const deleteStagePersonnel = useOperationStore((state) => state.deleteStagePersonnel);
  const cancelStageConsumption = useOperationStore((state) => state.cancelStageConsumption);
  const assignments = operation.stages?.[stage]?.personnelAssignments || [];
  const expenses = invoices.filter((invoice) => invoice.operationStage === stage.toUpperCase() && String(invoice.type).toUpperCase() === 'PURCHASE' && invoice.status !== 'Cancelled');
  const stageConsumptions = consumptions.filter((row) => row.stage === stage && row.status !== 'cancelled');
  const calculatedTotal = draft ? Math.round((Number(draft.dailyFee || 0) + Number(draft.extraFee || 0) + Number.EPSILON) * 100) / 100 : 0;
  const payableTotal = calculatedTotal;

  const choosePersonnel = (account, assignment = null) => {
    const existing = assignment || assignments.find((row) => (row.personelAccountId?._id || row.personelAccountId) === account._id);
    setDraft({
      _id: existing?._id, personelAccountId: account._id, name: account.name,
      dailyRateSnapshot: account.dailyRate ?? 0,
      dailyFee: existing?.dailyFee ?? existing?.dailyRateSnapshot ?? account.dailyRate ?? 0,
      extraFee: existing?.extraFee ?? existing?.additionalPayment ?? 0,
      currency: existing?.currency || account.currency || 'TRY'
    });
    setAccountOpen(false);
  };

  const edit = async (assignment) => {
    setEditing(true);
    try {
      const id = assignment.personelAccountId?._id || assignment.personelAccountId;
      const res = await axios.get(`/api/accounts/${id}`);
      choosePersonnel(res.data.data || res.data, assignment);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Personel kartı yüklenemedi.');
    } finally { setEditing(false); }
  };

  const save = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!Number.isFinite(calculatedTotal) || calculatedTotal < 0 || !Number.isFinite(payableTotal) || payableTotal < 0) {
      toast.error('Geçerli bir hak ediş tutarı girin.'); return;
    }
    submitting.current = true;
    setSaving(true);
    const result = await saveStagePersonnel(operation._id, stage, {
      _id: draft._id, personelAccountId: draft.personelAccountId,
      dailyFee: Number(draft.dailyFee || 0),
      extraFee: Number(draft.extraFee || 0),
      currency: draft.currency || 'TRY',
      totalAmount: calculatedTotal
    });
    if (result.success) {
      toast.success('Hak ediş kaydedildi.');
      setDraft(null);
      await onRefresh();
    } else toast.error(result.message);
    submitting.current = false;
    setSaving(false);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?._id || submitting.current) return;
    submitting.current = true;
    setSaving(true);
    const result = await deleteStagePersonnel(operation._id, stage, pendingDelete._id);
    if (result.success) {
      toast.success('Hak ediş silindi.');
      setPendingDelete(null);
      if (draft?._id === pendingDelete._id) setDraft(null);
      await onRefresh();
    } else toast.error(result.message);
    submitting.current = false;
    setSaving(false);
  };

  const cancelConsumption = async (row) => {
    if (!window.confirm('Bu sarf kaydı iptal edilecek ve stok iade edilecek. Devam edilsin mi?')) return;
    const result = await cancelStageConsumption(operation._id, row._id);
    if (result.success) {
      toast.success('Sarf iptal edildi, stok iade edildi.');
      await onRefresh();
    } else toast.error(result.message);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-black">Personel Hak Ediş Yönetimi</h2>{canAssign && <button disabled={saving || editing} type="button" onClick={() => setAccountOpen(true)} className="rounded-xl bg-blue-600 px-4 py-3 font-bold text-white">Personel Ekle</button>}</div>
        <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead><tr><th className="p-3">Personel</th><th>Günlük Ücret</th><th>Ek Ücret</th><th>Toplam Hak Ediş</th><th /></tr></thead><tbody>
          {assignments.map((row) => <tr key={row._id} className="border-t border-slate-100"><td className="p-3 font-bold">{row.personelAccountId?.name || row.personelAccountId?._id || row.personelAccountId || 'Silinmiş personel'}</td><td>{money(row.dailyFee ?? row.dailyRateSnapshot, row.currency)}</td><td>{money(row.extraFee ?? row.additionalPayment, row.currency)}</td><td>{money(row.totalAmount ?? row.calculatedTotal ?? row.payableTotal, row.currency)}</td><td className="whitespace-nowrap">{canAssign && row.personelAccountId && <button disabled={saving || editing} type="button" onClick={() => edit(row)} className="p-3 text-blue-600">Düzenle</button>}{canDeletePersonnel && <button disabled={saving || editing} type="button" onClick={() => setPendingDelete(row)} className="p-3 text-rose-600">Sil</button>}</td></tr>)}
          {!assignments.length && <tr><td colSpan={5} className="p-6 text-center text-slate-500">Bu aşamaya personel atanmadı.</td></tr>}
        </tbody></table></div>
        {draft && canAssign && <form onSubmit={save} className="mt-5 space-y-4 rounded-2xl bg-slate-50 p-5">
          <p className="font-bold">{draft.name} · Günlük ücret: {money(draft.dailyFee ?? draft.dailyRateSnapshot, draft.currency)}</p>
          <fieldset disabled={saving} className="grid gap-4 md:grid-cols-3">
            {moneyFields.map(([field, label]) => <label key={field}>{label}<NumericFormat
              className={inputClass} value={draft[field] === null || draft[field] === undefined ? 0 : draft[field]}
              thousandSeparator="." decimalSeparator="," allowedDecimalSeparators={[',', '.']} decimalScale={2} fixedDecimalScale allowNegative={false} inputMode="decimal"
              suffix={` ${draft.currency || 'TRY'}`} placeholder="0,00"
              onFocus={(e) => e.target.select()}
              isAllowed={({ floatValue }) => floatValue === undefined || Number.isFinite(floatValue)}
              onValueChange={({ floatValue }, source) => { if (source.source === 'event') setDraft((prev) => ({ ...prev, [field]: floatValue ?? 0 })); }}
            /></label>)}
            <label>
              Para Birimi
              <select
                className={inputClass}
                value={draft.currency || 'TRY'}
                onChange={(e) => setDraft((prev) => ({ ...prev, currency: e.target.value }))}
              >
                {currencyOptions.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
              </select>
            </label>
          </fieldset>
          <p className="text-lg font-black" aria-live="polite">Toplam Hak Ediş: {money(payableTotal, draft.currency)}</p>
          <p className="text-xs text-slate-500">Toplam hak ediş günlük ücret ile ek ücretin toplamıdır.</p>
          <div className="flex gap-3"><button disabled={saving} className="rounded-xl bg-blue-600 px-5 py-3 font-bold text-white">{saving ? 'Kaydediliyor...' : 'Kaydet'}</button><button disabled={saving} type="button" onClick={() => setDraft(null)}>Vazgeç</button></div>
        </form>}
      </section>
      <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-black">Aşama Masrafları</h2>{canExpense && <button type="button" onClick={() => setExpenseOpen(true)} className="rounded-xl bg-blue-600 px-4 py-3 font-bold text-white">Hızlı Masraf Ekle</button>}</div>
        <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead><tr><th className="p-3">Belge No</th><th>Tedarikçi</th><th>Tarih</th><th>Toplam</th></tr></thead><tbody>
          {expenses.map((invoice) => <tr key={invoice._id} className="border-t border-slate-100"><td className="p-3"><button type="button" onClick={() => onOpenInvoice(invoice)} className="font-bold text-blue-600">{invoice.invoiceNumber}</button></td><td>{invoice.accountId?.name || '-'}</td><td>{new Date(invoice.date).toLocaleDateString('tr-TR')}</td><td>{formatCurrency(invoice.grandTotal, invoice.currency)}</td></tr>)}
          {!expenses.length && <tr><td colSpan={4} className="p-6 text-center text-slate-500">{expensesLoading ? 'Faturalar yükleniyor...' : expensesUnavailable ? 'Faturalar yüklenemedi. Sayfayı yenileyin.' : 'Bu aşamada alış faturası yok.'}</td></tr>}
        </tbody></table></div>
      </section>
      <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-black">Sarf / Malzeme</h2>{canConsume && <button type="button" onClick={() => setConsumptionOpen(true)} className="rounded-xl bg-blue-600 px-4 py-3 font-bold text-white">Sarf / Malzeme Ekle</button>}</div>
        <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead><tr><th className="p-3">Malzeme</th><th>Miktar</th><th>Birim Fiyat</th><th>Toplam</th><th /></tr></thead><tbody>
          {stageConsumptions.map((row) => (
            <tr key={row._id} className="border-t border-slate-100">
              <td className="p-3 font-bold">{row.inventoryItemId?.name || 'Malzeme'}</td>
              <td>{row.quantity} {row.unit}</td>
              <td>{formatCurrency(row.unitPrice, row.currency)}</td>
              <td>{formatCurrency(row.totalAmount, row.currency)}</td>
              <td>{canConsume && <button type="button" onClick={() => cancelConsumption(row)} className="p-3 text-rose-600">İptal / İade</button>}</td>
            </tr>
          ))}
          {!stageConsumptions.length && <tr><td colSpan={5} className="p-6 text-center text-slate-500">Bu aşamada sarf kaydı yok.</td></tr>}
        </tbody></table></div>
      </section>
      <AccountSelectModal isOpen={accountOpen} onClose={() => setAccountOpen(false)} onSelect={choosePersonnel} accountType="EMPLOYEE" enableQuickCreate />
      {expenseOpen && <QuickExpenseModal operation={operation} stage={stage} onClose={() => setExpenseOpen(false)} onSaved={onRefresh} />}
      {consumptionOpen && <QuickMaterialConsumptionModal operation={operation} stage={stage} onClose={() => setConsumptionOpen(false)} onSaved={onRefresh} />}
      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-black">Hak edişi sil</h3>
            <p className="mt-3 text-sm text-slate-600">
              {pendingDelete.personelAccountId?.name || 'Bu personel'} için {stage} aşaması hak edişi silinecek. Personel cari ekstresi güncellenecektir. Devam edilsin mi?
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button disabled={saving} type="button" onClick={() => setPendingDelete(null)} className="rounded-xl px-4 py-3 font-bold text-slate-600">Vazgeç</button>
              <button disabled={saving} type="button" onClick={confirmDelete} className="rounded-xl bg-rose-600 px-4 py-3 font-bold text-white">{saving ? 'Siliniyor...' : 'Sil'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationStageTab;
